import React, { useState } from "react";
import { SUBSCRIPTION_PLANS } from "../config/stripeConfig";

// Group API plans by tier name (e.g. "starter", "pro")
function groupApiPlansByTier(apiPlans) {
	if (!apiPlans?.length) return null;

	const tiers = {};
	for (const plan of apiPlans) {
		if (plan.prix <= 0) continue; // Skip free/tester plans
		// Extract tier name: "Starter Mensuel" -> "starter", "Pro Annuel" -> "pro"
		const tierName = plan.libelle?.split(" ")[0]?.toLowerCase();
		if (!tierName) continue;
		if (!tiers[tierName]) tiers[tierName] = { name: tierName, monthly: null, yearly: null, features: [] };
		if (plan.dureeMois === 1) tiers[tierName].monthly = plan;
		else if (plan.dureeMois === 12) tiers[tierName].yearly = plan;
		// Take features from whichever variant has them
		if (plan.fonctionnalites?.length > 0 && tiers[tierName].features.length === 0) {
			tiers[tierName].features = plan.fonctionnalites.map(f => f.libelle);
		}
	}
	return Object.values(tiers);
}

const TIER_COLORS = { starter: "#4CAF50", pro: "#2196F3" };

function SubscriptionPlans({ currentPlanId, onSelectPlan, loading, apiPlans }) {
	const [billingPeriod, setBillingPeriod] = useState("monthly");

	const apiTiers = groupApiPlansByTier(apiPlans);
	// Has any plan with yearly option?
	const hasYearly = apiTiers?.some(t => t.yearly) ?? true;

	// Build cards: prefer API data, fallback to hardcoded SUBSCRIPTION_PLANS
	const cards = apiTiers
		? apiTiers.map((tier) => {
			const plan = billingPeriod === "yearly" && tier.yearly ? tier.yearly : (tier.monthly || tier.yearly);
			const monthlyPlan = tier.monthly || tier.yearly;
			const yearlyPlan = tier.yearly;
			return {
				idAbonnementType: plan.idAbonnementType,
				libelle: tier.name.charAt(0).toUpperCase() + tier.name.slice(1),
				prix: plan.prix,
				period: plan.dureeMois === 12 ? "/an" : "/mois",
				prixAnnuel: yearlyPlan?.prix,
				features: tier.features,
				color: TIER_COLORS[tier.name] || "#4CAF50",
				isPro: tier.name === "pro",
				billingPeriod: plan.dureeMois === 12 ? "yearly" : "monthly",
			};
		})
		: SUBSCRIPTION_PLANS.map((plan) => {
			const price = billingPeriod === "monthly" ? plan.prixMensuel : plan.prixAnnuel;
			return {
				idAbonnementType: plan.idAbonnementType,
				libelle: plan.libelle,
				prix: price,
				period: billingPeriod === "monthly" ? "/mois" : "/an",
				prixAnnuel: plan.prixAnnuel,
				features: plan.features,
				color: plan.color,
				isPro: plan.libelle === "Pro",
				billingPeriod,
			};
		});

	return (
		<div className="orange-banner" style={{ marginTop: 0 }}>
			<h2 className="orange-title" style={{ fontSize: "36px" }}>ABONNEMENTS</h2>

			{hasYearly && (
				<div className="billing-toggle billing-toggle-home">
					<span className={billingPeriod === "monthly" ? "active" : ""}>Mensuel</span>
					<label className="toggle-switch">
						<input
							type="checkbox"
							checked={billingPeriod === "yearly"}
							onChange={() => setBillingPeriod(billingPeriod === "monthly" ? "yearly" : "monthly")}
						/>
						<span className="toggle-slider"></span>
					</label>
					<span className={billingPeriod === "yearly" ? "active" : ""}>
						Annuel <span className="billing-savings">-17%</span>
					</span>
				</div>
			)}

			<div className="pricing-cards">
				{cards.map((card) => {
					const isCurrentPlan = currentPlanId === card.idAbonnementType;

					return (
						<div
							key={card.idAbonnementType}
							className={`pricing-card ${card.isPro ? "pricing-card-featured" : ""}`}
						>
							{isCurrentPlan && (
								<div className="subscription-plan-badge" style={{ backgroundColor: card.color }}>
									Plan actuel
								</div>
							)}

							<p className="pricing-name">{card.libelle}</p>

							<p className="pricing-price">
								{card.prix}€<span>{card.period}</span>
							</p>

							{card.prixAnnuel && billingPeriod === "yearly" && card.period === "/an" && (
								<p className="pricing-equivalent">
									soit {(card.prixAnnuel / 12).toFixed(2)}€/mois
								</p>
							)}

							<ul className="pricing-features">
								{card.features.map((feature, index) => (
									<li key={index}>{feature}</li>
								))}
							</ul>

							<button
								className="pricing-cta-button"
								style={{
									background: isCurrentPlan
										? "transparent"
										: `linear-gradient(135deg, ${card.color}, ${card.color}dd)`,
									borderColor: card.color,
									color: isCurrentPlan ? card.color : "#fff",
									opacity: isCurrentPlan ? 0.6 : 1,
								}}
								onClick={() => !isCurrentPlan && onSelectPlan({
									idAbonnementType: card.idAbonnementType,
									libelle: card.libelle,
									prix: card.prix,
									billingPeriod: card.billingPeriod,
								})}
								disabled={isCurrentPlan || loading}
							>
								{loading ? "Chargement..." :
									isCurrentPlan ? "Plan actuel" :
									"Choisir ce plan"}
							</button>
						</div>
					);
				})}
			</div>
		</div>
	);
}

export default SubscriptionPlans;
